import { alpha, Box, Button, Card, Grid, styled, Typography, useTheme, darken, Stack, ButtonBase, Tooltip, Popover, TextField as MuiTextField, Checkbox, 
  FormControlLabel, FormGroup, CircularProgress, FormHelperText } from "@mui/material";
import Chart from 'react-apexcharts';
import { ListControllerProps, ShowBase, useShowContext } from "ra-core";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { Content } from "@/components/content/Content";
import { FunctionField, TextField } from "@/components/field";
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import RowExpandClassificationRuleEdit from "@/components/securefiles/RowExpandClassificationRuleEdit";
import { OdResourcePageProps } from "@/types/resource";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { ClassificationRuleAmbinetDataCotnextProvider, useClassificationRuleAmbinetDataCotnext } from "@/components/securefiles/ClassificationRuleAmbientDataProvider";
import ClassificationLabel from "@/components/governance/labels/ClassificationLabel";
import { RecPatch } from "@/dataProvider/tools";
import { ClassificationRuleDto, ClassificationRulesDisplayDto, DataPoint, RuleRowState, TrialResultStatisDto } from "@/gen/api/securefile";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import { usePushBreadcrumb } from "@/utils/usePushBreadcrumb";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";
import { useResourceRegister } from "@/utils/resourceRegister";
import CreateOrEdit from "@/controller/CreateOrEdit";
import WithSave from "@/controller/WithSave";
import SelectiveContextProvider from "@/controller/SelectiveContextProvider";
import { ClassificationRulesContext, ClassificationRulesControllerProps } from "@/components/securefiles/ClassificationRulesContext";
import { crudGetOneEx } from "@/actions/crudFetchEx";
import { SecondaryClassificationResponseDto } from "@/gen/api/governance";


const pageMark = { label: 'File Classification Rules' }
const ruleSetConfirmRes = 'secure-files-clss-rule-set-confirm'
const ruleSetTrialRes = 'secure-files-clss-rule-trial'
const ruleSetTrialStatisRes = 'secure-files-clss-rule-trial-statis'
const ruleRevertRes = 'secure-files-clss-rule-revert'

const ClassificationRules: FC<OdResourcePageProps> = (props) => {
  usePushBreadcrumb(pageMark, { toMatch: '/secure-files/'})
  useResourceRegister({ name: ruleSetConfirmRes })
  useResourceRegister({ name: ruleSetTrialRes })
  useResourceRegister({ name: ruleSetTrialStatisRes })
  useResourceRegister({ name: ruleRevertRes })
  
  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <ClassificationRuleAmbinetDataCotnextProvider>
      <ShowBase id="edit" resource={props.resource} basePath="">
        <ClassificationRulesBody {...props} />
      </ShowBase>
    </ClassificationRuleAmbinetDataCotnextProvider>
  )
}

const clssRulesCtrlStatesInit = (record: ClassificationRulesDisplayDto|undefined) => {
  const statisData = record?.trialResultStatis?.comparedRuleSetVersion ? record?.trialResultStatis : null
  const trialId = !statisData ? (record?.trialResultStatis?.trialId ?? null) : null
  return ({
    SN: null as number|null,
    statisData,
    statisRefresh: {
      trialId,
      timer: null as number|null,
      interval: RefreshIntervals.s10,
    },
  })
}

const useClassificationRulesController = (): ClassificationRulesControllerProps => {
  const { record, refetch, loaded } = useShowContext<ClassificationRulesDisplayDto&RecPatch>()
  const data =
    useMemo(() => {
      const allRules = (record?.allRules ?? []).map(x => { (x as any).id = x.ruleId ; return x })
      const listItems = allRules.filter(r => 
           r.rowStatus === RuleRowState.Normal
        || r.rowStatus === RuleRowState.PreAdd
        || r.rowStatus === RuleRowState.PreDelete)
        .sort(defaultSorter)
      const changePendingIds = new Set(allRules.filter(r => r.rowStatus === RuleRowState.ChangePending).map(r => r.ruleId!))
      const currRuleSetIds = record?.currRuleSetIds ?? []
      const currRuleSetItems = allRules.filter(r => currRuleSetIds.indexOf(r.ruleId!) > -1).sort(defaultSorter)
      const newRuleSetItems = allRules.filter(r => 
           (r.rowStatus === RuleRowState.Normal && r.activeRule)
        || (r.rowStatus === RuleRowState.PreAdd && r.activeRule)
      ).sort(defaultSorter)
      const ruleSetChangeExists = newRuleSetItems.length !== currRuleSetIds.length || (new Set(newRuleSetItems.map(x => x.ruleId!))).union(new Set(currRuleSetIds)).size !== currRuleSetIds.length
      const listCtx : ListControllerProps<ClassificationRuleDto&RecPatch> = {
        basePath: "",
        ids: listItems.map(x => x.ruleId!),
        data: listItems.reduce((acc, x) => ({...acc, [x.ruleId!]: x}), {} as any),
        total: listItems.length ?? 0,
        loading: !record, loaded: !!record,
        page: 1, perPage: 10000,
        currentSort: { field: 'id', order: 'asc' },
        refetch,
      } as any
      return {listCtx, allRules, changePendingIds, currRuleSetItems, newRuleSetItems, ruleSetChangeExists}
    }, [record, refetch])
  const [states, setStates] = useState(() => clssRulesCtrlStatesInit(record))
  useEffect(() => {
    if (loaded) { setStates(clssRulesCtrlStatesInit(record)) }
  }, [loaded, record])
  const dispatch = useDispatch()
  useEffect(() => {
    const trialId = states.statisRefresh.trialId
    if (trialId && !states.statisRefresh.timer) {
      const timer = window.setInterval(() => {
        dispatch(crudGetOneEx(ruleSetTrialStatisRes, trialId, '', {
          onSuccess: (resp) => {
            const statisData: TrialResultStatisDto|undefined = resp?.data
            if (!statisData || !statisData.comparedRuleSetVersion) { return }
            setStates(v => {
              v.statisRefresh.timer && window.clearInterval(v.statisRefresh.timer);
              return ({...v,
                statisData,
                statisRefresh: {...v.statisRefresh,
                  trialId: null,
                  timer: null
                }
              })
            })
          }
        }))
      }, states.statisRefresh.interval)
      setStates(v => ({...v, statisRefresh: {...v.statisRefresh, timer}}))
    }
  }, [states.statisRefresh, dispatch])

  const timerRef = useRef(states.statisRefresh.timer)
  timerRef.current = states.statisRefresh.timer
  useEffect(() => () => {
    // clear timer when exit the page
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
    }
  }, [])
  
  const modifiers = useMemo(() => ({
    updateSN(sn: number|null) {
      setStates(v => ({...v, SN: sn}))
    },
    updateTrialId(trialId: number, recordsCount: number) {
      setStates(v => {
        v.statisRefresh.timer && window.clearInterval(v.statisRefresh.timer);
        return ({...v, 
          statisData: null,
          statisRefresh: {
            trialId,
            timer: null,
            interval: 
                recordsCount < 50000  ? RefreshIntervals.s5 
              : recordsCount < 80000  ? RefreshIntervals.s10 
              : recordsCount < 120000 ? RefreshIntervals.s30 
              : recordsCount < 300000 ? RefreshIntervals.m1 
              : RefreshIntervals.m2,
          }
        })
      })
    },
  }), [])
  return {
    ...data,
    record, 
    refetch,
    states,
    ...modifiers,
  }
}

const ClassificationRulesBody: FC<OdResourcePageProps> = ({
  options, resource
}) => {
  const ctrls = useClassificationRulesController()
  const { record, refetch, listCtx, changePendingIds, currRuleSetItems, newRuleSetItems, ruleSetChangeExists, states,
    updateSN, updateTrialId
  } = ctrls
  
  const dispatch = useDispatch()
  const { classifications } = useClassificationRuleAmbinetDataCotnext()
  
  const theme = useTheme()
  const rowStyles = (d:ClassificationRuleDto&RecPatch) => {
    if (d.rowStatus === RuleRowState.Normal) { return undefined }
    return {
      backgroundColor: 
        d.rowStatus === RuleRowState.PreDelete ? alpha(theme.palette.error.light, 0.1)
        : d.rowStatus === RuleRowState.PreAdd ? (
          d.predecessorId != null && changePendingIds.has(d.predecessorId)
          ? alpha(theme.palette.warning.light, 0.1)
          : alpha(theme.palette.success.light, 0.05))
        : undefined
    }
  }
  const rowTag = (x:ClassificationRuleDto) => (div?: HTMLDivElement) => {
    if (!div) { return }
    const tr = div.closest('tr.MuiTableRow-root')
    if (!tr) { return }
    tr.classList.add(`sn-${x.serialNumber}`)
  }

  const seriesData = states.statisData ? collectSeriesData(states.statisData, classifications) : []
  const chartOptions = createGroupedBarChartOptions(seriesData)

  return (
    <Content pageName="File Classification Rules" resourceName="File Classification Rules" breadcrumbs={options?.breadcrumbs}
      mainActions={
        <Button
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={() => dispatch(push(`/${resource}/create`))}
        >
          Create Rule
        </Button>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2, 
        ...(states.SN && {
          [`& .sn-${states.SN}`]: { boxShadow: '0 0 8px 4px gold', 
            '&.MuiTableRow-root': { position: 'relative', zIndex: theme => theme.zIndex.tooltip }
          }
        })
      }} >
        {ruleSetChangeExists &&
          <Grid item xs={12}>
            <SelectiveContextProvider
              ProviderComponent={ClassificationRulesContext.Provider}
              useContextFn={() => ctrls}
              selector={() => [currRuleSetItems, newRuleSetItems, changePendingIds, updateSN, updateTrialId]}
            >
              <DiffView currRuleSetItems={currRuleSetItems} newRuleSetItems={newRuleSetItems} changePendingIds={changePendingIds} updateSN={updateSN} />
            </SelectiveContextProvider>
          </Grid>}
        {ruleSetChangeExists && (states.statisData || states.statisRefresh.trialId) &&
          <Grid item xs={12}>
            {states.statisData 
              ? <>
                  <Typography variant="body2" color="info" marginBottom={-2}>Trial Result on {states.statisData?.trialCount} records</Typography>
                  <Chart height="200" type="bar" {...(chartOptions as any)} />
                </>
              : states.statisRefresh.timer ? 
                <Stack direction="row" justifyContent="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" component="span" marginLeft={2}>Trial Run is in progress.</Typography>
                </Stack>
              : null}
          </Grid>}
        <Grid item xs={12}>
          <Card>
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  expand={<RowExpandClassificationRuleEdit ctrls={ctrls} />}
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& td.column-displayName span.MuiTypography-root' : { maxWidth:300, wordBreak:'break-word' },
                    '& td.column-comment span.MuiTypography-root' : { maxWidth:500, wordBreak:'break-word' },
                    '& tbody.MuiTableBody-root.datagrid-body.RaDatagrid-tbody:after': {
                      content: '""',
                      display: 'block',
                      height: theme => theme.spacing(1),
                    }
                  }}
                  rowStyle={rowStyles}
                  {...listCtx}
                >
                  <FunctionField label="Rule Number" source="serialNumber" sortable={false} 
                    render={(d?:ClassificationRuleDto&RecPatch) => {
                      return (
                        <Box ref={rowTag(d!)}>
                          <Typography variant="subtitle2" color="info.main">#{d?.serialNumber}</Typography>
                        </Box>
                      )
                    }}
                  />
                  <TextField label="Name" source="displayName" sortable={false} />
                  <FunctionField label="Description" source="comment" sortable={false} 
                    render={(d?:ClassificationRuleDto&RecPatch) => (
                      <Typography variant="body2" component="pre" sx={{ lineBreak: 'anywhere', textWrap: 'auto' } as any}>{d?.comment}</Typography>
                    )}
                  />
                  <FunctionField source="targetClassification" label="Classification" sortable={false} 
                    render={(d?:ClassificationRuleDto&RecPatch) => {
                      const clss = classifications.find(c => c.secondaryClassificationId === d?.targetClassification)
                      if (!clss) { return null }
                      return <ClassificationLabel variant={clss.primaryClassification!} text={clss.name!} />;
                    }}
                  />
                  <FunctionField label="Priority" source="priority" sortable={false} 
                    render={(d?:ClassificationRuleDto&RecPatch) => {
                      return (
                        <Typography variant="body2" component="span">
                          {d?.priority}
                          {!(d?.activeRule) &&
                            <Typography color="error" component="span"> / Inactive</Typography>}
                        </Typography>)
                    }}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
            {/* {//@ts-ignore
              <DefaultPagination {...listCtx} />} */}
            {/* do we really want a pager ?? */}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default ClassificationRules;

const defaultSorter = (a: ClassificationRuleDto, b: ClassificationRuleDto) => a.priority === b.priority ? (b.ruleId! - a.ruleId!) : (a.priority! - b.priority!)

const RefreshIntervals = {
  s5: 5 * 1000,
  s10: 10 * 1000,
  s15: 15 * 1000,
  s30: 30 * 1000,
  m1: 1 * 60 * 1000,
  m2: 2 * 60 * 1000,
  m5: 5 * 60 * 1000,
}

const DiffView : FC<{
  currRuleSetItems: ClassificationRuleDto[], 
  newRuleSetItems: ClassificationRuleDto[], 
  changePendingIds: Set<number>,
  updateSN: (sn: number | null) => void
}> = () => {
  const {record, currRuleSetItems, newRuleSetItems, changePendingIds, updateSN, refetch, updateTrialId} = useContext(ClassificationRulesContext)
  const [states, setStates] = useState({
    trialRecordsCount: Math.min(10000, record?.totalFileEntryCount!),
    anchorEl1: null as HTMLButtonElement|null,
    anchorEl2: null as HTMLButtonElement|null,
    confirmOptions: {
      applyChangeToExistingFiles: true,
      overwriteManualClassification: false,
    }
  })

  const count_p = (ls: ClassificationRuleDto[]) => 
    ls.reduce((acc, x) => {
      let item = acc[acc.length - 1]
      if (!item || item.p !== x.priority) {
        item = { p: x.priority!, count: 0 } as any;
        acc.push(item)
      }
      item.count += 1
      return acc
    }, [] as PriorityItemGroup[])  
  const colDefs = [...count_p(currRuleSetItems), ...count_p(newRuleSetItems)].sort((a, b) => a.p === b.p ? b.count - a.count : a.p - b.p)
    .reduce((acc, x) => { 
      if (acc[acc.length - 1]?.p !== x.p) { acc.push(x) }
      return acc
    }, [] as PriorityItemGroup[])
  const colCount = colDefs.map(x => x.count).reduce((a, b) => a+b, 0)
  const genGroup = (ls: ClassificationRuleDto[]) =>
    colDefs.map(d => ({...d, items: ls.filter(x => x.priority === d.p)}) as PriorityItemGroup)
  const pgCurrRuleSet = genGroup(currRuleSetItems)
  const pgNewRuleSet = genGroup(newRuleSetItems)
  const classNames = (x: ClassificationRuleDto) => {
    const bag = ['item', `sn-${x.serialNumber}`] 
    bag.push(RuleRowState[x.rowStatus!])
    if (changePendingIds.has(x.predecessorId!)) { bag.push('from-change') }
    if (!x.activeRule) { bag.push('inactive') }
    else if (currRuleSetItems.indexOf(x) < 0) {
      bag.push('activated')
    }
    return bag.join(' ')
  }

  const handleClose = () => { setStates(v => ({...v, anchorEl1: null, anchorEl2: null}))}
  const trialHandlers = useDefaultSavedHandlers("Trial Run Schedule")
  const saveHandlers = useDefaultSavedHandlers("Apply Change")
  // refetch && trialHandlers.onSuccess.merge(refetch)
  refetch && saveHandlers.onSuccess.merge(refetch)
  
  const renderGroup = (isCurr: boolean) => (g:PriorityItemGroup) => (
    <Box key={g.p} className={`gspan-${g.count} gspan`}>
      {g.items.map(x => (
        <Tooltip title={
          <>
            {x.rowStatus === RuleRowState.PreAdd ? [(changePendingIds.has(x.predecessorId!) ? 'UPDATED' : 'ADDED'), <br />]
            :x.rowStatus === RuleRowState.PreDelete ? ['REMOVED', <br />]
            :x.rowStatus === RuleRowState.ChangePending ? ['UPDATED', <br />]
            :!x.activeRule ? ['DEACTIVATED', <br />]
            :!isCurr && currRuleSetItems.indexOf(x) < 0 ? ['ACTIVATED', <br />]
            : undefined}
            {x.displayName!}
          </>
        }>
          <Stack key={x.ruleId} className={classNames(x)} component={ButtonBase} justifyContent="center" alignItems="center"
            onMouseEnter={() => updateSN(x.serialNumber!)}
            onMouseLeave={() => updateSN(null)}
          >
            <Typography variant="caption" >
              #{x.serialNumber}
            </Typography>
          </Stack>
        </Tooltip>
      ))}
    </Box>
  )
  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" sx={{ alignSelf: 'end' }} gap={1}>
        {Boolean(record?.totalFileEntryCount && record?.currRuleSetDataReady) &&
          <Button color="primary" size="small" variant="outlined" sx={{width:120}}
            onClick={(e) => setStates(v => ({...v, anchorEl1:e.currentTarget}))}
          >
            Trial Run...
          </Button>}
        <Button color="primary" size="small" variant="outlined" sx={{width:120}}
          onClick={(e) => setStates(v => ({...v, anchorEl2:e.currentTarget}))}
        >
          Apply Changes
        </Button>
        <Popover id={'pop-trial-run'} anchorOrigin={{ vertical: 'bottom', horizontal: 'right', }} transformOrigin={{ vertical: 'top', horizontal: 'right'}}
          open={!!states.anchorEl1}
          anchorEl={states.anchorEl1}
          onClose={handleClose}
        >
          <Box m={2} width={300}>
            <MuiTextField label="Trial Records Count" fullWidth size="small" type="number" sx={{mb: 2}}
              inputProps={{ step: 1000, min: 1000, }}
              value={states.trialRecordsCount}
              onChange={(e) => {
                if (e.target.value === '') { setStates(v => ({...v, trialRecordsCount: '' as any})); return; }
                let n = Number.parseInt(e.target.value)
                n = Math.round(isNaN(n) ? Math.min(10000, record?.totalFileEntryCount!) 
                  : n < 1 ? 1 
                  : n > record?.totalFileEntryCount! ? record?.totalFileEntryCount! 
                  : n)
                setStates(v => ({...v, trialRecordsCount: n}))
              }}
              error={Boolean(states.trialRecordsCount === ('' as any))}
              helperText={
                states.trialRecordsCount === ('' as any) ? 'Please input a number' :
                `Approximately ${Math.floor(states.trialRecordsCount * 100 / record!.totalFileEntryCount!)}% of all data.`}
            />
            <CreateOrEdit resource={ruleSetTrialRes} basePath="" {...trialHandlers}>
              <WithSave 
                render={({save}) => (
                  <Button color="primary" variant="contained" fullWidth disabled={states.trialRecordsCount === ('' as any)}
                    onClick={() => {
                      handleClose()
                      const trialRecordsCount = states.trialRecordsCount
                      save({ recordsCount: trialRecordsCount }, {
                        onSuccess: (resp: any) => {
                          updateTrialId(resp.data.id, trialRecordsCount)
                        },
                      })
                    }} 
                  >
                    Run Trial
                  </Button>
                )}
              />
            </CreateOrEdit>
          </Box>
        </Popover>
        <Popover id={'pop-apply-change'} anchorOrigin={{ vertical: 'bottom', horizontal: 'right', }}  transformOrigin={{ vertical: 'top', horizontal: 'right'}}
          open={!!states.anchorEl2}
          anchorEl={states.anchorEl2}
          onClose={handleClose}
        >
          <Box m={2} width={300}>
            <FormGroup sx={{ mb: 2 }}>
              <FormControlLabel label="Apply Changes to Existing Files" 
                control={<Checkbox 
                  checked={states.confirmOptions.applyChangeToExistingFiles} 
                  onChange={(e) => setStates(v => ({...v, confirmOptions: {...v.confirmOptions, 
                    applyChangeToExistingFiles: e.target.checked,
                    ...(!e.target.checked && { overwriteManualClassification: false })
                  }}))}
                  />} 
              />
              <FormHelperText sx={{mt:-0.5}}>Apply the changes to the classification rules will trigger a new round of evaluation and classification of all files unless they were manually classified before. This will take some time to complete.</FormHelperText>
              {states.confirmOptions.applyChangeToExistingFiles &&
                <>
                  <FormControlLabel label="Override Manual Classification" 
                    control={<Checkbox 
                      checked={states.confirmOptions.overwriteManualClassification}
                      onChange={(e) => setStates(v => ({...v, confirmOptions: {...v.confirmOptions, 
                        overwriteManualClassification: e.target.checked
                      }}))}
                      />} 
                  />
                  <FormHelperText sx={{mt:-0.5}}>Choose this option if you would like to apply the new classification rules to the files that were manually classified before. Please be certain before choosing this option because all manual classification will be overridden.</FormHelperText>
                </>
                }
            </FormGroup>
            <CreateOrEdit resource={ruleSetConfirmRes} basePath="" {...saveHandlers}>
              <WithSave 
                render={({save}) => (
                  <Button variant="contained" color="primary" fullWidth 
                    onClick={() => {
                      handleClose()
                      save(states.confirmOptions)
                    }}
                  >
                    Apply
                  </Button>
                )}
              />
            </CreateOrEdit>
          </Box>
        </Popover>
      </Stack>
      <Stack direction="row">
        <Stack direction="column" justifyContent="space-evenly" width={80} mr={1}>
          <Typography variant="subtitle1" color="text.primary" textAlign="right" lineHeight={1.2}>Effective Rules</Typography>
          <Typography variant="subtitle1" color="text.primary" textAlign="right" lineHeight={1.2}>Unapplied Changes</Typography>
        </Stack>
        <DiffViewRoot>
          <Box sx={{
            gridTemplateColumns: theme => `repeat(${colCount}, ${theme.spacing(wItemUnit)})`,
            width: theme => theme.spacing(wItemUnit * colCount + gapUnit * (colCount - 1)),
          }}>
            {pgCurrRuleSet.map(renderGroup(true))}
            {pgNewRuleSet.map(renderGroup(false))}
          </Box>
        </DiffViewRoot>
      </Stack>
    </Stack>
  )
}

interface PriorityItemGroup {
  p: number,
  count: number,
  items: ClassificationRuleDto[],
}

const wItemUnit = 8
const gapUnit = 1

const DiffViewRoot = styled(Box)(({theme}) => {
  const wItem = theme.spacing(wItemUnit)
  const wItemInner = theme.spacing(wItemUnit - 2)
  const gap = theme.spacing(gapUnit)
  const colorHighlight = (s:any) => ({
    backgroundColor: (theme.palette as any)[s].light, 
    color: (theme.palette as any)[s].contrastText,
    opacity: 0.6,
    '&:hover': { opacity: 1 }
  })
  const indicator = (color:string) => ({
    '& > span.MuiTypography-root.MuiTypography-caption:after': {
      content: '""',
      borderBottom: `${theme.spacing(2.5)} solid ${color}`,
      borderRight: `${theme.spacing(2.5)} solid transparent`,
      position: "absolute",
      bottom: 0,
      left: 0,
      borderBottomLeftRadius: theme.spacing(0.5),
    }
  })
  return ({
    flex: '1 1 auto',
    overflowY: 'auto',
    backgroundColor: darken(theme.palette.background.default, 0.05),
    '& > div.MuiBox-root': {
      display: 'grid',
      gridTemplateRows: `repeat(2, ${wItem})`,
      gap,
      '& .gspan': {
        display: 'flex',
        flexDirection: 'row',
        gap,
        background: `radial-gradient(${theme.palette.background.paper}, ${darken(theme.palette.background.default, 0.05)})`,
        '& .item': {
          flex: `0 0 ${wItemInner}`,
          height: wItemInner,
          margin: theme.spacing(1),
          borderRadius: theme.spacing(0.5),
        },
      },
      ...([...new Array(50)].map((n, i) => i + 1)
        .reduce((acc, n) => ({...acc, [`& .gspan-${n}`]: { gridColumnEnd: `span ${n}` } }), {})),
      
    },
    '& .Normal, .PreDelete, .ChangePending': {
      backgroundColor: theme.palette.divider,
      opacity: 0.4,
    },
    '& .PreAdd': colorHighlight('success'),
    '& .PreAdd.from-change': colorHighlight('warning'),
    '& .PreDelete': indicator(theme.palette.error.main),
    '& .ChangePending': indicator(theme.palette.warning.main),
    '& .Normal.inactive': indicator(theme.palette.action.disabled),
    '& .Normal.activated': indicator(theme.palette.success.light),
  })
})

interface MarkerChartDatapoint {
  x: string;
  y: number;
  value: number;
}

const createMarkedBarChartOptions = (seriesData: (MarkerChartDatapoint|null)[]) => {
  const leftName = 'Current', rightName = 'Trial'
  const leftColor = '#00E396', rightColor = '#775DD0'
  const data = seriesData.map(d => d && ({ x: d.x, y: d.y, goals: [{ name: rightName, strokeWidth: 5, strokeColor: rightColor, value: d.value }] }))
  const chartOptions = {
    series: [ { name: leftName, data } ],
    options: {
      chart: { toolbar: { show: false }, },
      plotOptions: { bar: { horizontal: true, }},
      colors: [leftColor],
      dataLabels: { formatter: (val: any, opt: any) => undefined }, // don't need dataLabels
      legend: {
        show: true,
        showForSingleSeries: true,
        customLegendItems: [leftName, rightName],
        markers: {
          fillColors: [leftColor, rightColor]
        }
      }
    },
  };
  return chartOptions
}

const createGroupedBarChartOptions = (seriesData: (MarkerChartDatapoint|null)[]) => {
  const {xArr, yArr, categories} = seriesData.reduce((acc, d) => {
    acc.xArr.push(d?.y)
    acc.yArr.push(d?.value)
    acc.categories.push(d?.x)
    return acc
  }, {xArr: [] as any[], yArr: [] as any[], categories: [] as any[]})
  const chartOptions = {
    series: [{ data: xArr, name: 'Current' }, { data: yArr, name: 'Trial' }],
    options: {
      chart: { toolbar: { show: false }, },
      plotOptions: {
        bar: { horizontal: true, dataLabels: { position: 'top', }, }
      },
      dataLabels: { formatter: (val: any, opt: any) => undefined }, // don't need dataLabels
      stroke: { show: true, width: 1, colors: ['#fff'] },
      tooltip: { shared: true, intersect: false },
      xaxis: { categories, },
    },
  };
  return chartOptions
}

const collectSeriesData = (dto: TrialResultStatisDto, classifications: SecondaryClassificationResponseDto[]): (MarkerChartDatapoint|null)[] => {
  if (!dto.ruleSetStatis || !dto.trialStatis) { return [] }

  const groupPoints = (ls: DataPoint[]) =>
    ls.reduce((acc, x) => {
      const arr = acc[x.classification!] ?? (acc[x.classification!] = [])
      arr.push(x)
      return acc
    }, {} as Record<string, DataPoint[]>)
  const add = (a: number, b: number) => a+b
  const rBag = groupPoints(dto.ruleSetStatis)
  const tBag = groupPoints(dto.trialStatis)
  const clssBag = [...new Set([...Object.keys(rBag), ...Object.keys(tBag)])].reduce((acc, id) => {
    const clss = classifications.find(c => c.secondaryClassificationId === +id)
    if (clss) {
      acc[id] = clss
    }
    return acc
  }, {} as Record<string, SecondaryClassificationResponseDto>)
  const mPoints = Object.keys(clssBag).map(clssId => {
    const clss = clssBag[clssId]
    return {
      x: `${clss.primaryClassificationText} - ${clss.name}`,
      y: (rBag[clssId] ?? []).map(x => x.count!).reduce(add, 0),
      value: (tBag[clssId] ?? []).map(x => x.count!).reduce(add, 0),
    } as MarkerChartDatapoint
  })
  const piontUnspecified = {
    x: 'Unspecified',
    y: Math.max(dto.trialCount! - dto.igoredCount! - mPoints.map(p => p.y).reduce(add, 0), 0),
    value: Math.max(dto.trialCount! - dto.igoredCount! - mPoints.map(p => p.value).reduce(add, 0), 0),
  } as MarkerChartDatapoint
  const mPoints_5 = mPoints.map(p => [Math.abs(p.y - p.value), p] as const).filter(x => x[0] > 0).sort((a, b) => b[0] - a[0]).slice(0, 5).map(x => x[1]).sort((a, b) => b.y - a.y)
  return [...mPoints_5, piontUnspecified]
}

